/**
 * "Neste dia": o que aconteceu num 9 de setembro qualquer de outro ano.
 *
 * É o `lib/core/services/on_this_day_service.dart` inteiro. Ele junta três
 * lugares — o álbum, a lista de cinema e a conversa do Refúgio — pergunta a
 * cada um o que caiu no mesmo dia e mês de hoje em anos anteriores, e devolve
 * tudo numa fileira só, da lembrança mais nova para a mais velha.
 *
 * As três buscas de lá são filtros sobre o que já está na mão (`getPhotos...`,
 * `getWatchList...`, `getMessages...` leem a coleção local e peneiram): nenhuma
 * abre rede. Aqui é igual — quem chama passa o que o `usarColecao` já entregou.
 *
 * As palavras que aparecem na tela ficam como estão lá: a legenda do bilhete é
 * `'Foto da galeria'`, a do cinema é `'Vocês viram juntos'` ou `'Você colocou
 * na lista'`, e a da conversa é `'... said'`, em inglês, porque é o que o outro
 * app mostra.
 */

import { fotosDesteDia, type Foto } from '../galeria/foto';
import { mensagensDesteDia, type Mensagem } from '../refugio/mensagem';
import { lerData } from '../nucleo/tempo';
import type { Documento } from '../types/dados';
import { estaVisto, posterDoDoc, type DocMidia } from '../types/cinema';

/** De onde a lembrança veio. É o `OnThisDaySource`. */
export type FonteDaLembranca = 'galeria' | 'cinema' | 'refugio';

/** O `OnThisDayMemory`, sem o `original`: ninguém do porte precisa do cru. */
export interface Lembranca {
  id: string;
  fonte: FonteDaLembranca;
  quando: Date;
  titulo: string;
  legenda: string;
  /** Endereço da foto ou cartaz do filme, já resolvido. Vazio quer dizer sem capa. */
  imagem: string;
  /** O `yearsAgo`: quantos aniversários essa lembrança já fez. */
  anos: number;
}

/** O `.take(3)` do `_getChatMemories`: a conversa entra sem tomar a fileira. */
export const QUANTAS_FALAS = 3;

/** O corte de 80 letras do `_getChatMemories`, com as reticências dele. */
const CORTE_DA_FALA = 80;

/** O `yearsAgo`: só a diferença dos anos, que é como está lá. */
function anosAtras(quando: Date, hoje: Date): number {
  return hoje.getFullYear() - quando.getFullYear();
}

/**
 * O `getWatchListFromThisDay`: mesmo dia e mês de outro ano, e cada título uma
 * vez só — o `seen` de lá, que fica com a primeira aparição e descarta as
 * outras (o mesmo filme pode estar na lista das duas pessoas).
 *
 * O `monthDay` gravado no documento serviria de peneira barata, mas o ano é
 * preciso de todo jeito para o `anos`, então a data é aberta uma vez só e
 * responde as duas perguntas.
 */
export function midiasDesteDia(
  documentos: ReadonlyArray<Documento<DocMidia>>,
  hoje = new Date(),
): Array<Documento<DocMidia>> {
  const vistos = new Set<number>();
  const achadas: Array<Documento<DocMidia>> = [];

  for (const doc of documentos) {
    const quando = lerData(doc.dados.addedAt);
    if (!quando) continue;
    if (quando.getMonth() !== hoje.getMonth()) continue;
    if (quando.getDate() !== hoje.getDate()) continue;
    if (quando.getFullYear() === hoje.getFullYear()) continue;
    if (vistos.has(doc.dados.tmdbId)) continue;
    vistos.add(doc.dados.tmdbId);
    achadas.push(doc);
  }

  return achadas;
}

/** O que cada fonte tem para dar. Quem não tiver uma delas, deixa de fora. */
export interface FontesDaLembranca {
  fotos?: ReadonlyArray<Foto>;
  midias?: ReadonlyArray<Documento<DocMidia>>;
  mensagens?: ReadonlyArray<Mensagem>;
}

/**
 * O `getAllMemories`: as três fontes juntas, da mais nova para a mais velha.
 *
 * Lá o `Future.wait` corre as três em paralelo e o `catch` devolve lista vazia
 * se qualquer uma cair. Aqui não há o que cair — é filtro sobre memória — então
 * o que sobrou do `try` foi só a ordem.
 */
export function lembrancasDeHoje(fontes: FontesDaLembranca, hoje = new Date()): Lembranca[] {
  const lembrancas: Lembranca[] = [];

  for (const foto of fotosDesteDia([...(fontes.fotos ?? [])], hoje)) {
    lembrancas.push({
      id: `galeria:${foto.id}`,
      fonte: 'galeria',
      quando: foto.quando,
      titulo: foto.legenda || 'Uma lembrança em foto',
      legenda: 'Foto da galeria',
      // O `GalleryService.displayUrl` é a identidade: o endereço já é o que a
      // imagem pede.
      imagem: foto.endereco,
      anos: anosAtras(foto.quando, hoje),
    });
  }

  for (const doc of midiasDesteDia(fontes.midias ?? [], hoje)) {
    const quando = lerData(doc.dados.addedAt);
    if (!quando) continue;
    lembrancas.push({
      id: `cinema:${doc.id}`,
      fonte: 'cinema',
      quando,
      titulo: doc.dados.title ?? '',
      legenda: estaVisto(doc.dados.status ?? '') ? 'Vocês viram juntos' : 'Você colocou na lista',
      imagem: posterDoDoc(doc.dados.posterPath ?? ''),
      anos: anosAtras(quando, hoje),
    });
  }

  for (const fala of mensagensDesteDia([...(fontes.mensagens ?? [])], hoje).slice(
    0,
    QUANTAS_FALAS,
  )) {
    lembrancas.push({
      id: `refugio:${fala.id}`,
      fonte: 'refugio',
      quando: fala.quando,
      titulo:
        fala.texto.length > CORTE_DA_FALA
          ? `${fala.texto.substring(0, CORTE_DA_FALA)}...`
          : fala.texto,
      legenda: `${fala.quem} said`,
      imagem: '',
      anos: anosAtras(fala.quando, hoje),
    });
  }

  return lembrancas.sort((a, b) => b.quando.getTime() - a.quando.getTime());
}
